import noop from 'lodash/noop';
import isEqual from 'lodash/isEqual';
import React, {FocusEvent, Component, ChangeEvent} from 'react';
import {
    DefaultFieldRenderer,
    FieldFactoryProps,
    FieldRendererIntersectionProps,
    StaticTextField,
} from 'forms';
import {CollectionFieldExtraProps as ExtraProps, FieldSchema} from '../types';

type Props<
    V extends {},
    E extends HTMLElement
> = OmitFromIndexed<FieldRendererIntersectionProps<V, E>, 'error'> & ExtraProps;

type FFProps<
    V extends {},
    E extends HTMLElement
> = ExtraProps & OmitFromIndexed<FieldFactoryProps<V, E>, 'error'> & {
    onValueSubmitSub: (value: any, name: string) => void;
    onValueResetSub: (value: any, name: string) => void;
};

type FocusEv = FocusEvent<HTMLElement>;

interface State<V extends {}> {
    value: V;
}

/**
 * Represents a collection of values as a single value.
 *
 * The values are rendered inside a bootstrap row. A column class can be passed
 * to be used for all sub-fields using the `fieldProps` property.
 */
export default class CollectionField<
    V extends {},
    E extends HTMLElement
> extends Component<Props<V, E>, State<V>> {
    public static defaultProps: Partial<Props<{}, HTMLElement>> = {
        Renderer: DefaultFieldRenderer,
        name: null,
        onBlurCaptureSub: noop,
        onBlurSub: noop,
        onFocusCaptureSub: noop,
        onFocusSub: noop,
        onValueChange: noop,
        onValueReset: noop,
        onValueSubmit: noop,
        schema: [],
        value: {}, // FIXME: This allows required props to be missing
    };

    public state: State<V> = {
        value: this.props.value,
    };

    private handleSubValueChange = (
        subValue: any,
        _: any,
        n: string,
        e: ChangeEvent<HTMLElement>
    ) => {
        const oldValue = this.state.value;
        const value = { ...this.state.value, [n]: subValue };
        this.setState({ value });
        this.props.onValueChange(value, oldValue, this.props.name, e);
    }

    private handleSubValueSubmit = (v: any, n: string, f: FieldSchema) => {
        (f.props.onValueSubmit || noop)(v, n);
        const {props} = this;
        props.onValueSubmit({ ...this.state.value, [n]: v}, props.name);
    }

    private handleSubValueReset = (v: any, n: string, f: FieldSchema) => {
        (f.props.onValueReset || noop)(v, n);
        const {props} = this;
        props.onValueReset({ ...this.state.value, [n]: v }, props.name);
    }

    private handleSubFocus = (e: FocusEv, f: FieldSchema) => {
        (f.props.onFocus || noop)(e);
        this.props.onFocusSub(e);
    }

    private handleSubFocusCapture = (e: FocusEv, f: FieldSchema) => {
        (f.props.onFocusCapture || noop)(e);
        this.props.onFocusCaptureSub(e);
    }

    private handleSubBlur = (e: FocusEv, f: FieldSchema) => {
        (f.props.onBlur || noop)(e);
        this.props.onBlurSub(e);
    }

    private handleSubBlurCapture = (e: FocusEv, f: FieldSchema) => {
        (f.props.onBlurCapture || noop)(e);
        this.props.onBlurCaptureSub(e);
    }

    public componentWillReceiveProps(nextProps: Props<V, E>) {
        if (!isEqual(this.state.value, nextProps.value)) {
            this.setState({ value: nextProps.value });
        }
    }

    public render() {
        return <this.props.Renderer
            {...this.props}
            factory={this.renderFactory.bind(this)}
        />;
    }

    private renderFactory (props: FFProps<V, E>) {
        const {state} = this;
        return <>
            {props.schema.map((f) => {
                if (f.hidden) {
                    return;
                }
                const Type = f.Type || StaticTextField;
                return <Type key={`${f.name}`}
                    Renderer={props.SubRenderer}
                    disabled={props.disabled}
                    format={(!f.Type) ? f.format : undefined}
                    label={f.label}
                    name={`${f.name}`}
                    onBlur={(e) => this.handleSubBlur(e, f)}
                    onBlurCapture={(e) => this.handleSubBlurCapture(e, f)}
                    onFocus={(e) => this.handleSubFocus(e, f)}
                    onFocusCapture={(e) => this.handleSubFocusCapture(e, f)}
                    onValueChange={this.handleSubValueChange}
                    onValueReset={(v, n) => this.handleSubValueReset(v, n, f)}
                    onValueSubmit={(v, n) => this.handleSubValueSubmit(v, n, f)}
                    value={state.value[f.name]}
                    error={props.error && props.error[f.name]}
                    {...f.props}
                />;
            })}
        </>;
    }
}
